
// A list of colors used for image hotspot markers.
// When editing, make sure to update the corresponding sass variables in hotspots.scss,
// (I still haven't found a way to unify these lists).
const hotspotColors = {
    red: "#f93446",
    teal: "#31d3c8",
    green: "#21db9c",
    blue: "#409fd7",
    lightblue: "#296bd7",
    navy: "#1d3f6a",
    yellow: "#ffe215",
    orange: "#fd7e14",
    pink: "#ffb7c4",
    purple: "#836ef1",
    white: "#ffffff",
    gray: "#dee2e6",
    tan: "#d1cdc5",
    transparent: "transparent",
}

// A mapping of glyphicons icon names to panel types.
const panelTypeIcons = {
    custom: "icon-glyphicons-basic-picture",
    multiselect: "icon-glyphicons-basic-square-empty-check",
    singleselect: "icon-glyphicons-basic-circle-selected",
    multichoice: "icon-glyphicons-basic-directions-arrow",
    textarea: "icon-glyphicons-basic-square-edit",
    linked_response: "icon-glyphicons-basic-eye",
    vitals: "icon-glyphicons-basic-pulse",
    score: "icon-glyphicons-basic-medal",
    chat: "icon-glyphicons-basic-user-chat",
    chat_v2: "icon-glyphicons-basic-chat-conversation",
    discovery: "icon-glyphicons-basic-tasks",
    step_order: "icon-glyphicons-basic-sort-numerically",
    "3d_video": "icon-glyphicons-basic-view-full",
    image_hotspots: "icon-glyphicons-basic-target",
    categories: "icon-glyphicons-basic-block-move",
    notepad: "icon-glyphicons-basic-notes",
    voice_recording: "icon-glyphicons-basic-mic",
    tabs: "icon-glyphicons-basic-layers",
    custom_form: "icon-glyphicons-basic-cheque",
}

const panelTypeLabels = {
    custom: "Display",
    multichoice: "Buttons",
    multiselect: "Multiple Selection",
    singleselect: "Multiple Choice",
    step_order: "Order the Steps",
    textarea: "Text Response",
    linked_response: "Review Response",
    score: "Learner Results",
    vitals: "Vitals Monitor",
    chat: "Chat Interaction",
    chat_v2: "Custom GPT",
    discovery: "Discovered Item List",
    '3d_video': "360° Video",
    image_hotspots: "Image Hotspots",
    categories: "Categories",
    notepad: "Notepad",
    voice_recording: "Voice Response",
    tabs: "Tabs",
    custom_form: "Custom Form",
}

// These settings match the primary and body colors defined within the scss variables
// file.
const primaryColor = '#115fde'
const bodyColor = '#3a3f44'

// Global theme color sets used to style the learner experience.
const globalThemeOptions = {
    bluenavy: {
        primary: "#115fde",
        secondary: "#304b6c",
        background: "#e9ecf2",
        alertBgLightPercent: 56,
        textDarkenPercent: 0,
    },
    tealnavy: {
        primary: "#09a99d",
        secondary: "#304b6c",
        background: "#e9ecf2",
        alertBgLightPercent: 58,
        textDarkenPercent: 8,
    },
    purple: {
        primary: "#836ef1",
        secondary: "#4a4a8c",
        background: "#eaedf1",
        alertBgLightPercent: 50,
        textDarkenPercent: 1.5,
    },
    green: {
        primary: "#14a775",
        secondary: "#226251",
        background: "#eaedf1",
        alertBgLightPercent: 62,
        textDarkenPercent: 10,
    },
}

// A lookup of human readable names for each panel type, and defines which panel types
// are allowed on an end node.
const panelTypes = [
    {
        value: "custom",
        description: "Images, text, videos, audio, PDFs",
        paidOnly: false,
        beta: false,
        new: false,
        ai: true,
    },
    {
        value: "tabs",
        description: "Clickable tabs containing content",
        paidOnly: false,
        beta: false,
        new: false,
    },
    {
        value: "multichoice",
        description: "Interactive buttons with actions",
        paidOnly: false,
        beta: false,
        new: false,
    },
    {
        value: "textarea",
        description: "Free-text inquiry + evaluation",
        paidOnly: false,
        beta: false,
        new: false,
        ai: true,
    },
    {
        value: "chat_v2",
        description: "Chat-based conversation",
        paidOnly: false,
        beta: false,
        new: true,
        ai: true,
    },
    {
        value: "image_hotspots",
        description: "Interactive 2D or 360° image",
        paidOnly: false,
        beta: false,
        new: false,
    },
    {
        value: "voice_recording",
        description: "Voice recording with transcription",
        paidOnly: false,
        beta: false,
        new: false,
        ai: true,
    },
    {
        value: "multiselect",
        description: "Multi-select inquiry",
        paidOnly: false,
        beta: false,
        new: false,
    },
    {
        value: "singleselect",
        description: "Single-select inquiry",
        paidOnly: false,
        beta: false,
        new: false,
    },
    {
        value: "step_order",
        description: "Draggable steps",
        paidOnly: false,
        beta: false,
        new: false,
    },
    {
        value: "categories",
        description: "Category matching inquiry",
        paidOnly: false,
        beta: false,
        new: false,
    },
    {
        value: "custom_form",
        description: "Create a fillable form",
        paidOnly: false,
        beta: false,
        new: false,
    },
    {
        value: "linked_response",
        description: "Display a previous response",
        paidOnly: false,
        beta: false,
        new: false,
    },
    {
        value: "notepad",
        description: "Free-text notepad",
        paidOnly: false,
        beta: false,
        new: false,
    },
    {
        value: "3d_video",
        description: "Show a 360° video",
        paidOnly: false,
        beta: false,
        new: false,
    },
    {
        value: "vitals",
        description: "Dynamic vital signs",
        paidOnly: false,
        beta: false,
        new: false,
    },
    {
        value: "score",
        description: "Score & results summary",
        paidOnly: false,
        beta: false,
        new: false,
    },
    {
        value: "discovery",
        description: "Display discovered items",
        paidOnly: false,
        beta: false,
        new: false,
    },
]

const labelTranslations = {
    en: {
        continue: "Continue",
        submit: "Submit",
        responseSummary: "Response Summary",
        noResponse: "No Response Submitted",
        noResponses: "No Responses Submitted",
        yourChoices: "Your Choices",
        yourResponse: "Your Response",
        correctResponse: "Correct Response",
        typeYourResponse: "Type your response here...",
        editedResponse: "Edited Response",
        yourScore: "Your Score",
        close: "Close",
        successful: "Successful",
        unsuccessful: "Unsuccessful",
        explain: "Explain",
        downloadPDF: "Download PDF",
        chatConversation: "Chat Conversation",
        timeMetrics: "Time Performance",
        yourTime: "Your Time",
        correctCategory: "Correct Category",
        returnToLibrary: "Return to Library",
        returnToPage: "Return to Page",
        returnToStart: "Return to Start",
        done: "Done",
        restart: "Restart",
        resume: "Resume Session",
        sessionInProgress: "Session In Progress",
        resumeSessionMessage: "It looks like you currently have a session in progress for this scenario.",
        closeBrowserTabs: "If you have this scenario open in another browser tab, please close it now.",
        notepadEntries: "Notes",
        noNotes: "No notes to display.",
        notepad: "Notepad",
        resources: "Resources",
        openLearnerResources: "Learner Resources",
        openNotepad: "Notepad",
        requestSupport: "Request Support",
        submitFeedback: "Submit Feedback",
        enterFullscreen: "Enter Fullscreen",
        exitFullscreen: "Exit Fullscreen",
        clickToAddress: "Click to address",
        replay: "Replay",
        back: "Back",
        showTranscript: "Show Transcript",
        gotIt: "Got It",
        skipTour: "Skip Tour",
        next: "Next",
        responseSaved: "Response saved",
        responseSaving: "Saving response",
        notesSaved: "Notes saved",
        notesSaving: "Saving notes",
        hotspotsExplored: "explored",
        lookAround: "Click and drag to look around",
        recordingMicrophoneTap: "Press on the microphone to start recording",
        recordingReview: "Review your recording",
        aiAudio: "The voice you are hearing was generated using AI.",
        scrollDown: "Scroll down to see more",
        analyzing: "Analyzing",
        compareYourAnswer: "Compare your response with the instructor's answer",
        aiGeneratedFeedback: "AI-Generated Feedback",
        analyzingYourResponse: "Analyzing your response",
        youConversationWillAppearHere: "Your conversation will appear here",
        saySomethingHere: "Say something here",
        continueBtnExp: {
            videos: "Videos must finish before continuing",
            activities: "Complete activities on this screen before continuing",
            unfinished: "You have unfinished activities on this screen that require completion",
        },
        submitBtnDisabledExp: "Complete activities on this screen before submitting your responses",
        instructorFeedback: "Instructor Feedback",
    },
    fr: {
        continue: "Continuer",
        submit: "Soumettre",
        responseSummary: "Résumé de votre performance",
        noResponse: "Aucune option sélectionnée",
        noResponses: "Aucune option sélectionnée",
        yourChoices: "Vos sélections",
        yourResponse: "Votre soumission",
        correctResponse: "Soumission correcte",
        typeYourResponse: "Tapez votre réponse ici...",
        editedResponse: "Soumission modifiée",
        yourScore: "Votre note",
        close: "Fermer",
        successful: "Terminé",
        unsuccessful: "Infructueux",
        explain: "Explique",
        downloadPDF: "Télécharger le PDF",
        chatConversation: "Conversation",
        timeMetrics: "Métriques de temps",
        yourTime: "Votre temps",
        correctCategory: "Bonne catégorie",
        returnToLibrary: "Retour à la bibliothèque",
        returnToPage: "Retour à la page",
        returnToStart: "Retourner au début",
        done: "Terminer",
        restart: "Recommencer",
        resume: "Reprendre votre session",
        sessionInProgress: "Séance en cours",
        resumeSessionMessage: "Vous avez actuellement une session en cours pour ce scénario.",
        closeBrowserTabs: "Si ce scénario est ouvert dans un autre onglet du navigateur, veuillez le fermer maintenant.",
        notepadEntries: "Bloc-notes",
        noNotes: "Aucune note à afficher.",
        notepad: "Bloc-notes",
        resources: "Ressources",
        openLearnerResources: "Les ressources de l'apprenant",
        openNotepad: "Bloc-notes",
        requestSupport: "Demander de l'aide",
        submitFeedback: "Soumettre des commentaires",
        enterFullscreen: "Passer en mode plein écran",
        exitFullscreen: "Quitter le mode plein écran",
        clickToAddress: "Cliquez pour adresser",
        replay: "Rejouer",
        back: "Retourner",
        showTranscript: "Montrer la transcription",
        gotIt: "J'ai compris",
        skipTour: "Passer le tour",
        next: "Procéder",
        responseSaved: "Réponse enregistrée",
        responseSaving: "Enregistrer la réponse",
        notesSaved: "Notes enregistrées",
        notesSaving: "Enregistrer des notes",
        hotspotsExplored: "explorés",
        lookAround: "Cliquez et faites glisser pour regarder autour de vous",
        recordingMicrophoneTap: "Cliquez ou appuyez sur le microphone pour démarrer l'enregistrement",
        recordingReview: "Vérifiez votre enregistrement",
        aiAudio: "La voix que vous entendez a été générée à l'aide de l'IA.",
        scrollDown: "Défiler vers le bas",
        analyzing: "En cours d'analyse",
        compareYourAnswer: "Comparez votre réponse avec celle de l'instructeur",
        aiGeneratedFeedback: "Commentaires générés par l'IA",
        analyzingYourResponse: "Analyser votre réponse",
        youConversationWillAppearHere: "Votre conversation apparaîtra ici",
        saySomethingHere: "Dis quelque chose ici",
        continueBtnExp: {
            videos: "Les vidéos doivent être terminées avant de continuer",
            activities: "Terminez les activités sur cet écran avant de continuer",
            unfinished: "Vous avez des activités inachevées sur cet écran qui doivent être complétées",
        },
        submitBtnDisabledExp: "Effectuez les activités sur cet écran avant de soumettre vos réponses",
        instructorFeedback: "Commentaires de l'instructeur",
    }
}

const learnerTourStepTranslations = {
    en: {
        resources: {
            header: "Find learning resources here",
            content: "These resources will be available throughout the learning experience.",
        },
        notepad: {
            header: "Take notes here",
            content: "Your notepad will be available on every screen.",
        },
        comments: {
            header: "Add your comments here",
            content: "Add your comments & feedback to each screen as you move along.",
        },
        score: {
            header: "Your current score",
            content: "Your score will update as you collect (or lose) points.",
        },
        navigation: {
            header: "Navigate between screens",
            content: "Use this dropdown to navigate to any screen in the learning experience.",
        },
    },
    fr: {
        resources: {
            header: "Trouvez des ressources pour les apprenants ici",
            content: "Ces ressources seront disponibles tout au long de l'expérience d'apprentissage.",
        },
        notepad: {
            header: "Prenez des notes ici",
            content: "Votre bloc-notes sera disponible sur chaque écran.",
        },
        comments: {
            header: "Ajoutez vos commentaires ici",
            content: "Ajoutez vos commentaires et vos feedbacks à chaque écran au fur et à mesure de votre progression.",
        },
        score: {
            header: "Votre score actuel",
            content: "Votre score se mettra à jour au fur et à mesure que vous gagnerez (ou perdrez) des points.",
        },
        navigation: {
            header: "Naviguer entre les écrans",
            content: "Utilisez cette liste déroulante pour accéder à n'importe quel écran de l'expérience d'apprentissage.",
        },
    }    
}

const continueRequirementOptions = [
    {
        label: "No correct answers required to continue",
        value: "none",
    },
    {
        label: "At least one correct answer required to continue",
        value: "one",
    },
    {
        label: "All correct answers required to continue",
        value: "all",
    },
    {
        label: "Custom correct answer requirement",
        value: "custom",
    },
]

const roleNames = {
    student: "Learner",
    author: "Author",
    admin: "Admin",
}

export { primaryColor, bodyColor, globalThemeOptions, hotspotColors, panelTypeIcons, panelTypeLabels, panelTypes, learnerTourStepTranslations, labelTranslations, continueRequirementOptions, roleNames }